// src/lib/push.js
// Utility per Service Worker + Web Push (VAPID)

// --- Config ---
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";

// --- Helpers ---
function base64urlToUint8Array(base64String) {
  // converte una chiave VAPID public base64url in Uint8Array
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

// Rilevazione supporto: ritorna "ok" se siamo pronti, altrimenti motivo
export function pushSupportReason() {
  if (typeof window === "undefined") return "no-window";
  if (!("serviceWorker" in navigator)) return "no-sw";
  // 🔧 FIX: parentesi in più rimossa
  if (!("Notification" in window)) return "no-notifications";

  // iOS Safari espone le push solo per le web app INSTALLATE (Aggiungi a Home)
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (!("PushManager" in window)) {
    return isIOS ? "ios-need-install" : "no-push";
  }
  return "ok";
}

// Registra (se serve) /sw.js e restituisce la registration
export async function ensureSW() {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service Worker non supportato");
  }
  // prova a recuperare una registration esistente sullo scope radice
  let reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) {
    // fallback: cerca qualunque SW attivo
    reg = await navigator.serviceWorker.getRegistration();
  }
  if (!reg) {
    // registra il nostro SW pubblico
    reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  }
  // attesa "pronto"
  await navigator.serviceWorker.ready;
  return reg;
}

// Ritorna l'eventuale subscription corrente (o null)
export async function getCurrentSubscription() {
  try {
    await ensureSW();
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Salva le preferenze push lato server (puoi usarlo dal setup iniziale o da Impostazioni).
 * settings shape atteso:
 * {
 *   notification_checkin_enabled: boolean,
 *   notification_checkin_time: "HH:MM",
 *   notification_exercises_enabled: boolean,
 *   notification_exercise_time: "HH:MM"
 * }
 */
export async function savePushSettings({ userId = "anon", tz, settings }) {
  try {
    tz = tz || Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome";
    await fetch("/api/push-settings", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId, tz, settings }),
    });
  } catch (e) {
    console.warn("savePushSettings failed:", e);
  }
}

/**
 * Richiede permessi + crea la subscription e la salva lato server.
 * Puoi passarci userId e (opzionale) settings per salvarli subito anche dal setup iniziale.
 */
export async function subscribePush({ userId, settings } = {}) {
  const reason = pushSupportReason();
  if (reason !== "ok") {
    if (reason === "ios-need-install") {
      throw new Error("Su iPhone installa l’app nella Schermata Home e riaprila.");
    }
    throw new Error("Web Push non supportato su questo dispositivo (" + reason + ").");
  }

  if (!VAPID_PUBLIC_KEY) {
    throw new Error("VITE_VAPID_PUBLIC_KEY mancante");
  }

  await ensureSW();
  const reg = await navigator.serviceWorker.ready;

  // Permesso
  let perm = Notification.permission;
  if (perm !== "granted") {
    perm = await Notification.requestPermission();
    if (perm !== "granted") {
      throw new Error(
        perm === "denied"
          ? "Permesso notifiche negato: abilitalo dalle impostazioni del sito."
          : "Permesso non concesso."
      );
    }
  }

  // Crea (o recupera) la subscription
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64urlToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  // Salva lato server (subscription + tz + userId)
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome";
  const payload = {
    subscription: sub,            // PushSubscription JSON
    tz,                           // time zone dell'utente
    userId: userId || "anon",     // id utente
    ua: navigator.userAgent,
  };

  // endpoint principale
  try {
    await fetch("/api/push-subscribe", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    // fallback legacy se nella tua app esistono ancora questi endpoint
    try {
      await fetch("/api/push-sub", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (e2) {
      try {
        await fetch("/api/save-sub", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
        });
      } catch {
        // ignora
      }
    }
  }

  // (Opzionale) se fornisci settings, salvali subito (utile nel setup iniziale)
  if (settings && typeof settings === "object") {
    await savePushSettings({ userId, tz, settings });
  }

  return sub;
}

// Notifica locale immediata (per test UI/OS; non è una push)
export async function localTestNotification() {
  try {
    await ensureSW();
    const reg = await navigator.serviceWorker.ready;
    reg.active?.postMessage({
      type: "LOCAL_NOTIFY",
      title: "Test notifica",
      body: "Suono/vibrazione di prova",
      url: "/",
    });
  } catch (e) {
    console.warn("Local test notification failed:", e);
  }
}
